import { Skeleton, SvgIcon } from '@mui/material';
import { BlockCategory, BlockCustomIconVariant } from 'flow-schema';
import { type ComponentType } from 'react';

import { isDefined } from 'common';

import { useBlockTypeMetadata } from '../ReactFlow/Nodes/Configs/useBlockTypeMetadata';
import { type UiBlockType } from '../ReactFlow/types';

import { ReactComponent as AiBlockSvg } from './assets/aiBlock.svg';
import { ReactComponent as DataBlockSvg } from './assets/dataBlock.svg';
import { ReactComponent as ExperienceBlockSvg } from './assets/experienceBlock.svg';
import { ReactComponent as ExportBlockSvg } from './assets/exportBlock.svg';
import { ReactComponent as IntegrationBlockSvg } from './assets/integrationBlock.svg';
import { ReactComponent as IteratorBlockSvg } from './assets/iteratorBlock.svg';
import { ReactComponent as LogicBlockSvg } from './assets/logicBlock.svg';
import { ReactComponent as ObsoleteBlockSvg } from './assets/old/obsoleteBlock.svg';
import { ReactComponent as PlaceholderSvg } from './assets/old/placeholderBlock.svg';
import { ReactComponent as ScriptBlockSvg } from './assets/scriptBlock.svg';
import { ReactComponent as SkillBlockSvg } from './assets/skillBlock.svg';
import { ReactComponent as StartBlockSvg } from './assets/startBlock.svg';
import { ReactComponent as SubFlowBlockSvg } from './assets/subFlowBlock.svg';
import { ReactComponent as TextBlockSvg } from './assets/textBlock.svg';

type UserSelectableBlockCategory = Exclude<
  BlockCategory,
  BlockCategory.Start | BlockCategory.Obsolete | BlockCategory.Placeholder
>;
export const USER_SELECTABLE_BLOCK_CATEGORIES: UserSelectableBlockCategory[] = [
  BlockCategory.Ai,
  BlockCategory.Data,
  BlockCategory.Experience,
  BlockCategory.Export,
  BlockCategory.Integration,
  BlockCategory.Logic,
  BlockCategory.Skill,
];

const BLOCK_ICONS_BY_CATEGORY: Record<BlockCategory, ComponentType> = {
  [BlockCategory.Start]: StartBlockSvg,
  [BlockCategory.Logic]: LogicBlockSvg,
  [BlockCategory.Data]: DataBlockSvg,
  [BlockCategory.Experience]: ExperienceBlockSvg,
  [BlockCategory.Integration]: IntegrationBlockSvg,
  [BlockCategory.Ai]: AiBlockSvg,
  [BlockCategory.Export]: ExportBlockSvg,
  [BlockCategory.Skill]: SkillBlockSvg,
  [BlockCategory.Obsolete]: ObsoleteBlockSvg,
  [BlockCategory.Placeholder]: PlaceholderSvg,
};

const BLOCK_ICONS_BY_CUSTOM_ICON_VARIANT: Record<BlockCustomIconVariant, ComponentType> = {
  [BlockCustomIconVariant.Iterator]: IteratorBlockSvg,
  [BlockCustomIconVariant.SubFlow]: SubFlowBlockSvg,
  [BlockCustomIconVariant.Script]: ScriptBlockSvg,
  [BlockCustomIconVariant.Text]: TextBlockSvg,
};

interface BlockCategoryIconProps {
  blockCategory: BlockCategory;
  blockCustomIconVariant?: BlockCustomIconVariant | null;
  variant?: 'default' | 'large' | 'small';
}

export function BlockCategoryIcon(props: BlockCategoryIconProps) {
  const { blockCategory, blockCustomIconVariant, variant } = props;
  return (
    <SvgIcon
      component={
        isDefined(blockCustomIconVariant)
          ? BLOCK_ICONS_BY_CUSTOM_ICON_VARIANT[blockCustomIconVariant]
          : BLOCK_ICONS_BY_CATEGORY[blockCategory]
      }
      inheritViewBox
      fontSize={variant === 'default' ? undefined : variant}
    />
  );
}

const BLOCK_TYPE_ICON_SKELETON_SIZE_BY_VARIANT: Record<
  NonNullable<BlockCategoryIconProps['variant']>,
  number
> = {
  default: 24,
  large: 32,
  small: 16,
};

interface BlockTypeCategoryIconProps {
  uiBlockType: UiBlockType;
  variant?: 'default' | 'large' | 'small';
}

export function BlockTypeCategoryIcon(props: BlockTypeCategoryIconProps) {
  const { uiBlockType, variant } = props;
  const blockContentResults = useBlockTypeMetadata({ uiBlockType });

  if (blockContentResults.isPending) {
    return (
      <Skeleton
        variant="rectangular"
        width={BLOCK_TYPE_ICON_SKELETON_SIZE_BY_VARIANT[variant ?? 'default']}
        height={BLOCK_TYPE_ICON_SKELETON_SIZE_BY_VARIANT[variant ?? 'default']}
      >
        <BlockCategoryIcon blockCategory={BlockCategory.Placeholder} variant={variant} />
      </Skeleton>
    );
  }
  if (blockContentResults.isError) {
    return <BlockCategoryIcon blockCategory={BlockCategory.Placeholder} variant={variant} />;
  }
  return (
    <BlockCategoryIcon
      blockCategory={blockContentResults.data.blockCategory}
      blockCustomIconVariant={blockContentResults.data.customIconVariant}
      variant={variant}
    />
  );
}
